import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import MfaEnrollment from './pages/MfaEnrollment';
import Dashboard from './pages/Dashboard';
import Assets from './pages/Assets';
import AssetDetail from './pages/AssetDetail';
import Software from './pages/Software';
import Remediation from './pages/Remediation';
import Incidents from './pages/incidents/Incidents';
import IncidentDetail from './pages/incidents/IncidentDetail';
import Reports from './pages/Reports';
import Security from './pages/Security';
import Hygiene from './pages/Hygiene';
import AntivirusPosture from './pages/AntivirusPosture';
import NetworkHealthPage from './pages/NetworkHealthPage';
import Users from './pages/Users';
import Baseline from './pages/Baseline';
import LogicalAssets from './pages/LogicalAssets';
import Tenants from './pages/Tenants';
import AgentDownload from './pages/AgentDownload';
import AssetCreate from './pages/AssetCreate';
import Locations from './pages/Locations';
import Licenses from './pages/Licenses';
import Providers from './pages/Providers';
import Notifications from './pages/Notifications';
import Aprobadores from './pages/Aprobadores';
import Epm from './pages/Epm';
import Pam from './pages/Pam';
import AuditLog from './pages/AuditLog';
import CloudCosts from './pages/CloudCosts';
import ManagerDashboard from './pages/ManagerDashboard';
import KnowledgeBase from './pages/KnowledgeBase';
import Profile from './pages/Profile';
import UsbControl from './pages/UsbControl';
import HardwareHealth from './pages/HardwareHealth';
import CloudSync from './pages/CloudSync';
import Anomalies from './pages/Anomalies';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuditReports from './pages/AuditReports';
import AIReports from './pages/AIReports';
import Integrations from './pages/Integrations';
import Policies from './pages/Policies';
import NetworkAudit from './pages/NetworkAudit';
import NetworkDiscovery from './pages/NetworkDiscovery';
import MatrizCriticidad from './pages/MatrizCriticidad';
import MDM from './pages/MDM';
import Changes from './pages/Changes';
import Automation from './pages/Automation';
import ChangeDetail, { ChangeNew } from './pages/ChangeDetail';
import MonitoringCloud from './pages/MonitoringCloud';
import MonitoringOnPremise from './pages/MonitoringOnPremise';
import Disponibilidad from './pages/Disponibilidad';
import Parches from './pages/Parches';
import Directorio from './pages/Directorio';
import RevealPassword from './pages/RevealPassword';
import AwsInventory from './pages/AwsInventory';
import Layout from './components/Layout';
import './App.css';

function MobilesPage() { return <Navigate to="/assets?agent_type=android" replace />; }

function IndexRedirect() {
  const { user } = useAuth();
  if (user?.role === 'usuario') return <Navigate to="/incidents" replace />;
  if (user?.role === 'rrhh') return <Navigate to="/directorio" replace />;
  return <Dashboard />;
}

function hasRole(user, role) {
  return user?.role === role || (user?.extra_roles || []).includes(role);
}

function PrivateRoute({ children, adminOnly, managerForbidden, usuarioForbidden, seguridadForbidden, viewerForbidden }) {
  const { user, loading, mfaEnroll } = useAuth();
  const location = useLocation();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  // 2FA obligatorio pendiente: se corta acá, antes que cualquier otra regla de rol.
  // No es un Navigate a una ruta (sería esquivable escribiendo otra URL): se reemplaza
  // el contenido de TODA ruta privada por la pantalla de activación.
  if (mfaEnroll) return <MfaEnrollment />;
  // RRHH: rol acotado — solo Directorio, tickets y perfil (todo lo demás se redirige).
  if (user.role === 'rrhh') {
    const ok = ['/directorio', '/incidents', '/profile'].some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
    if (!ok) return <Navigate to="/directorio" />;
  }
  // Usuario WhatsApp: solo puede ver incidentes
  if (user.role === 'usuario' && usuarioForbidden === true)
    return <Navigate to="/incidents" />;
  // Rol visor: excluido de secciones donde no aplica solo-lectura (ej. proveedores)
  if (user.role === 'viewer' && viewerForbidden === true)
    return <Navigate to="/" />;
  // Rol seguridad: sin acceso a costos ni administración
  if (user.role === 'seguridad' && seguridadForbidden === true)
    return <Navigate to="/" />;
  if (adminOnly && !hasRole(user, 'admin')) return <Navigate to="/" />;
  // Gerente puro (sin otros roles) no accede a páginas técnicas
  if (managerForbidden && user.role === 'manager' && !(user.extra_roles || []).some(r => ['admin','technician'].includes(r)))
    return <Navigate to="/manager" />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reveal-password" element={<RevealPassword />} />
          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<IndexRedirect />} />
            <Route path="manager" element={<PrivateRoute usuarioForbidden><ManagerDashboard /></PrivateRoute>} />
            <Route path="assets" element={<PrivateRoute usuarioForbidden><Assets /></PrivateRoute>} />
            <Route path="assets/new" element={<PrivateRoute usuarioForbidden><AssetCreate /></PrivateRoute>} />
            <Route path="assets/:id" element={<PrivateRoute usuarioForbidden><AssetDetail /></PrivateRoute>} />
            <Route path="software" element={<PrivateRoute usuarioForbidden><Software /></PrivateRoute>} />
            <Route path="remediation" element={<PrivateRoute usuarioForbidden><Remediation /></PrivateRoute>} />
            <Route path="logical-assets" element={<PrivateRoute usuarioForbidden><LogicalAssets /></PrivateRoute>} />
            <Route path="providers" element={<PrivateRoute usuarioForbidden viewerForbidden><Providers /></PrivateRoute>} />
            <Route path="notifications" element={<PrivateRoute adminOnly usuarioForbidden><Notifications /></PrivateRoute>} />
            <Route path="approvers" element={<PrivateRoute adminOnly usuarioForbidden><Aprobadores /></PrivateRoute>} />
            <Route path="epm" element={<PrivateRoute adminOnly usuarioForbidden><Epm /></PrivateRoute>} />
            <Route path="pam" element={<PrivateRoute adminOnly usuarioForbidden><Pam /></PrivateRoute>} />
            <Route path="incidents" element={<Incidents />} />
            <Route path="incidents/:id" element={<IncidentDetail />} />
            <Route path="reports" element={<PrivateRoute usuarioForbidden seguridadForbidden><Reports /></PrivateRoute>} />
            <Route path="security" element={<PrivateRoute usuarioForbidden><Security /></PrivateRoute>} />
            <Route path="hygiene" element={<PrivateRoute usuarioForbidden><Hygiene /></PrivateRoute>} />
            <Route path="antivirus" element={<PrivateRoute usuarioForbidden><AntivirusPosture /></PrivateRoute>} />
            <Route path="network-health" element={<PrivateRoute usuarioForbidden><NetworkHealthPage /></PrivateRoute>} />
            <Route path="users" element={<PrivateRoute adminOnly usuarioForbidden><Users /></PrivateRoute>} />
            <Route path="directorio" element={<PrivateRoute usuarioForbidden seguridadForbidden viewerForbidden><Directorio /></PrivateRoute>} />
            <Route path="audit-log" element={<PrivateRoute adminOnly usuarioForbidden><AuditLog /></PrivateRoute>} />
            <Route path="baseline" element={<PrivateRoute adminOnly usuarioForbidden><Baseline /></PrivateRoute>} />
            <Route path="tenants" element={<PrivateRoute adminOnly usuarioForbidden><Tenants /></PrivateRoute>} />
            <Route path="agents" element={<PrivateRoute adminOnly usuarioForbidden><AgentDownload /></PrivateRoute>} />
            <Route path="locations" element={<PrivateRoute adminOnly usuarioForbidden><Locations /></PrivateRoute>} />
            <Route path="licenses" element={<PrivateRoute usuarioForbidden seguridadForbidden><Licenses /></PrivateRoute>} />
            <Route path="cloud-costs" element={<PrivateRoute usuarioForbidden seguridadForbidden><CloudCosts /></PrivateRoute>} />
            <Route path="knowledge-base" element={<PrivateRoute usuarioForbidden><KnowledgeBase /></PrivateRoute>} />
            <Route path="profile" element={<Profile />} />
            <Route path="usb-control" element={<PrivateRoute usuarioForbidden><UsbControl /></PrivateRoute>} />
            <Route path="hardware-health" element={<PrivateRoute usuarioForbidden><HardwareHealth /></PrivateRoute>} />
            <Route path="cloud-sync" element={<PrivateRoute usuarioForbidden><CloudSync /></PrivateRoute>} />
            <Route path="anomalies" element={<PrivateRoute usuarioForbidden><Anomalies /></PrivateRoute>} />
            <Route path="audit-reports" element={<PrivateRoute usuarioForbidden><AuditReports /></PrivateRoute>} />
            <Route path="ai-reports" element={<PrivateRoute usuarioForbidden><AIReports /></PrivateRoute>} />
            <Route path="integrations" element={<PrivateRoute adminOnly usuarioForbidden><Integrations /></PrivateRoute>} />
            <Route path="policies" element={<PrivateRoute adminOnly usuarioForbidden><Policies /></PrivateRoute>} />
            <Route path="network-audit" element={<PrivateRoute usuarioForbidden><NetworkAudit /></PrivateRoute>} />
            <Route path="network-discovery" element={<PrivateRoute usuarioForbidden><NetworkDiscovery /></PrivateRoute>} />
            <Route path="matriz-criticidad" element={<PrivateRoute usuarioForbidden><MatrizCriticidad /></PrivateRoute>} />
            <Route path="mobiles" element={<PrivateRoute usuarioForbidden><MobilesPage /></PrivateRoute>} />
            <Route path="mdm" element={<PrivateRoute usuarioForbidden><MDM /></PrivateRoute>} />
            <Route path="changes" element={<PrivateRoute usuarioForbidden><Changes /></PrivateRoute>} />
            <Route path="automation" element={<PrivateRoute usuarioForbidden><Automation /></PrivateRoute>} />
            <Route path="changes/new" element={<PrivateRoute usuarioForbidden><ChangeNew /></PrivateRoute>} />
            <Route path="changes/:id" element={<PrivateRoute usuarioForbidden><ChangeDetail /></PrivateRoute>} />
            <Route path="monitoring/cloud" element={<PrivateRoute usuarioForbidden><MonitoringCloud /></PrivateRoute>} />
            <Route path="monitoring/onpremise" element={<PrivateRoute usuarioForbidden><MonitoringOnPremise /></PrivateRoute>} />
            <Route path="disponibilidad" element={<PrivateRoute usuarioForbidden><Disponibilidad /></PrivateRoute>} />
            <Route path="parches" element={<PrivateRoute usuarioForbidden><Parches /></PrivateRoute>} />
            <Route path="aws-inventory" element={<PrivateRoute usuarioForbidden><AwsInventory /></PrivateRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

